// Centralized case status styling utilities
import { SxProps, Theme } from '@mui/material/styles';

export type CaseStatus = 'OPEN' | 'UNDER_INVESTIGATION' | 'IN_REVIEW' | 'FILED' | 'CLOSED' | 'ARCHIVED';

export type StatusColorType = 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' | 'default';

/**
 * Get Material-UI color for case status (for basic Chip components)
 */
export const getCaseStatusColor = (status: CaseStatus): StatusColorType => {
  switch (status) {
    case 'OPEN':
      return 'primary';
    case 'UNDER_INVESTIGATION':
      return 'info'; // We'll override with custom sx for yellow
    case 'IN_REVIEW':
      return 'warning';
    case 'FILED':
      return 'success';
    case 'CLOSED':
    case 'ARCHIVED':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Get custom sx styling for case status (overrides Material-UI colors when needed)
 */
export const getCaseStatusSx = (status: CaseStatus): SxProps<Theme> => {
  switch (status) {
    case 'OPEN':
      return {
        backgroundColor: '#1976d2',
        color: 'white',
        '&:hover': { backgroundColor: '#1565c0' }
      };
    case 'UNDER_INVESTIGATION':
      return {
        backgroundColor: '#DAA520', // More goldish (goldenrod)
        color: 'white',
        '&:hover': { backgroundColor: '#B8860B' }
      };
    case 'IN_REVIEW':
      return {
        backgroundColor: '#ed6c02',
        color: 'white',
        '&:hover': { backgroundColor: '#e65100' }
      };
    case 'FILED':
      return {
        backgroundColor: '#2e7d32',
        color: 'white',
        '&:hover': { backgroundColor: '#1b5e20' }
      };
    case 'CLOSED':
    case 'ARCHIVED':
      return {
        backgroundColor: '#d32f2f',
        color: 'white',
        '&:hover': { backgroundColor: '#c62828' }
      };
    default:
      return {
        backgroundColor: '#757575',
        color: 'white'
      };
  }
};

/**
 * Get display label for case status
 */
export const getCaseStatusLabel = (status: CaseStatus): string => {
  switch (status) {
    case 'OPEN':
      return 'Open';
    case 'UNDER_INVESTIGATION':
      return 'Under Investigation';
    case 'IN_REVIEW':
      return 'In Review';
    case 'FILED':
      return 'Filed';
    case 'CLOSED':
      return 'Closed';
    case 'ARCHIVED':
      return 'Archived';
    default:
      return status;
  }
};

/**
 * Get background colors for DataGrid rows based on case status
 */
export const getCaseStatusRowColors = (status: CaseStatus) => {
  switch (status) {
    case 'FILED':
      return {
        background: 'rgba(76, 175, 80, 0.08)', // light green
        hover: 'rgba(76, 175, 80, 0.12)'
      };
    case 'UNDER_INVESTIGATION':
      return {
        background: 'rgba(218, 165, 32, 0.15)', // light goldish
        hover: 'rgba(218, 165, 32, 0.25)'
      };
    case 'CLOSED':
    case 'ARCHIVED':
      return {
        background: 'rgba(244, 67, 54, 0.08)', // light red
        hover: 'rgba(244, 67, 54, 0.12)'
      };
    case 'OPEN':
      return {
        background: 'rgba(33, 150, 243, 0.08)', // light blue
        hover: 'rgba(33, 150, 243, 0.12)'
      };
    case 'IN_REVIEW':
      return {
        background: 'rgba(255, 152, 0, 0.08)', // light orange
        hover: 'rgba(255, 152, 0, 0.12)'
      };
    default:
      return {
        background: 'inherit',
        hover: 'grey.100'
      };
  }
};